/**
 * 任务轮询 Composable
 * 封装任务提交和状态轮询的完整流程
 */

import { ref, computed, onUnmounted, type Ref, type ComputedRef } from 'vue'
import type { TaskService } from '../core/services/TaskService'
import type {
  TaskStatus,
  SubmitTaskParams,
  SubmitTaskResponse,
  TaskStatusResponse
} from '../types/task'

export interface UseTaskPollingOptions {
  /** 提交成功回调 */
  onSubmitted?: (response: SubmitTaskResponse) => void
  /** 状态变化回调 */
  onStatusChange?: (status: TaskStatus, result?: TaskStatusResponse) => void
  /** 进度回调 */
  onProgress?: (progress: number) => void
  /** 完成回调 */
  onComplete?: (result: TaskStatusResponse) => void
  /** 失败回调 */
  onFailed?: (result: TaskStatusResponse) => void
  /** 错误回调 */
  onError?: (error: Error) => void
}

export interface UseTaskPollingReturn {
  /** 当前任务序列号 */
  serialNo: Ref<string | null>
  /** 当前状态 */
  status: Ref<TaskStatus | null>
  /** 进度（0-100） */
  progress: Ref<number>
  /** 是否正在处理 */
  isProcessing: ComputedRef<boolean>
  /** 是否已完成 */
  isComplete: ComputedRef<boolean>
  /** 是否失败 */
  isFailed: ComputedRef<boolean>
  /** 结果 */
  result: Ref<TaskStatusResponse | null>
  /** 错误 */
  error: Ref<Error | null>
  /** 提交任务并轮询 */
  submit: (params: SubmitTaskParams) => Promise<TaskStatusResponse | null>
  /** 停止轮询 */
  stop: () => void
  /** 重置状态 */
  reset: () => void
}

/**
 * 创建任务轮询 Composable
 */
export function createUseTaskPolling(taskService: TaskService) {
  return function useTaskPolling(options: UseTaskPollingOptions = {}): UseTaskPollingReturn {
    const {
      onSubmitted,
      onStatusChange,
      onProgress,
      onComplete,
      onFailed,
      onError
    } = options

    // 状态
    const serialNo = ref<string | null>(null)
    const status = ref<TaskStatus | null>(null)
    const progress = ref(0)
    const result = ref<TaskStatusResponse | null>(null)
    const error = ref<Error | null>(null)

    // 计算属性
    const isProcessing = computed(() =>
      status.value === 'pending' || status.value === 'processing'
    )
    const isComplete = computed(() => status.value === 'success')
    const isFailed = computed(() => status.value === 'failed')

    /**
     * 提交任务并轮询
     */
    const submit = async (params: SubmitTaskParams): Promise<TaskStatusResponse | null> => {
      // 重置状态
      reset()

      try {
        const taskResult = await taskService.submitAndPoll(params, {
          onSubmitted: (response) => {
            serialNo.value = response.serial_no
            status.value = 'pending'
            onSubmitted?.(response)
          },
          onStatusChange: (newStatus, statusResult) => {
            status.value = newStatus
            onStatusChange?.(newStatus, statusResult)
          },
          onProgress: (newProgress) => {
            progress.value = newProgress
            onProgress?.(newProgress)
          }
        })

        result.value = taskResult

        if (taskResult.status === 'success') {
          onComplete?.(taskResult)
        } else if (taskResult.status === 'failed') {
          onFailed?.(taskResult)
        }

        return taskResult
      } catch (err) {
        error.value = err as Error
        onError?.(err as Error)
        return null
      }
    }

    /**
     * 停止轮询
     */
    const stop = () => {
      if (serialNo.value) {
        taskService.stopPolling(serialNo.value)
      }
    }

    /**
     * 重置状态
     */
    const reset = () => {
      stop()
      serialNo.value = null
      status.value = null
      progress.value = 0
      result.value = null
      error.value = null
    }

    // 组件卸载时停止轮询
    onUnmounted(() => {
      stop()
    })

    return {
      serialNo,
      status,
      progress,
      isProcessing,
      isComplete,
      isFailed,
      result,
      error,
      submit,
      stop,
      reset
    }
  }
}
